import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { useBreakpoints } from "@/hooks/use-breakpoints";
import { cn } from "@/lib/utils";

const Sheet = SheetPrimitive.Root;
const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;
const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = ({ ref, className, ...props }: React.ComponentProps<typeof SheetPrimitive.Overlay>) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/30 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
    ref={ref}
  />
);

const sheetVariants = cva(
  "fixed z-50 bg-neutral-700 text-neutral-50 max-h-[94svh] md:max-h-none h-auto flex flex-col transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        bottom:
          "inset-x-0 bottom-0 rounded-t-2xl data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        right:
          "inset-y-0 right-0 rounded-tl-2xl rounded-tr-none rounded-bl-2xl h-full md:w-[512px] data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
        center:
          "rounded-2xl inset-0 max-h-none m-auto w-80 md:w-[512px] data-[state=closed]:scale-out data-[state=open]:scale-in data-[state=closed]:duration-0",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

type SheetContentProps = React.ComponentProps<typeof SheetPrimitive.Content> & VariantProps<typeof sheetVariants>;

const SheetContent = ({ ref, className, children, side, ...props }: SheetContentProps) => {
  const breakpoints = useBreakpoints();

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        onOpenAutoFocus={e => e.preventDefault()}
        ref={ref}
        className={cn(sheetVariants({ side: side === "center" ? side : ["md", "lg", "xl"].includes(breakpoints) ? "right" : "bottom" }), className)}
        {...props}
      >
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
};

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "border-b border-neutral-500 p-3 text-center md:p-4",
      className,
    )}
    {...props}
  />
);

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex w-full gap-2 p-3 child:w-full md:p-4",
      className,
    )}
    {...props}
  />
);

const SheetTitle = ({ ref, className, ...props }: React.ComponentProps<typeof SheetPrimitive.Title>) => (
  <>
    <SheetPrimitive.Title
      ref={ref}
      className={cn("font-semibold", className)}
      {...props}
    />
    <SheetPrimitive.Description className="sr-only" aria-hidden>
      {props.children}
    </SheetPrimitive.Description>
  </>
);

const SheetDescription = ({ ref, className, ...props }: React.ComponentProps<typeof SheetPrimitive.Description>) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm", className)}
    {...props}
  />
);

const SheetForm = ({ ref, className, children, ...rest }: React.ComponentProps<"form">) => {
  return (
    <form className={cn("flex w-full grow flex-col gap-4 overflow-y-auto px-3 py-6 md:px-4", className)} {...rest} ref={ref}>
      {children}
    </form>
  );
};

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetForm,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
